import React, { useEffect, useMemo, useState } from 'react';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { CalendarRange } from 'lucide-react';
import { fmtDate } from './demoConstants';

const pad = (n) => String(n).padStart(2, '0');
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const lastDay = (y, m) => new Date(y, m, 0).getDate(); // m 1-based

// Indian FY: Apr–Mar. Quarters Q1 Apr-Jun … Q4 Jan-Mar
const fyStart = (d) => (d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1);
const qIndexOf = (d) => Math.floor(((d.getMonth() - 3 + 12) % 12) / 3); // 0..3
const quarterRange = (sy, qi) => {
  const startMonth = 3 + qi * 3; // 0-based month offset from Jan of sy
  const sYear = sy + Math.floor(startMonth / 12);
  const sMon = (startMonth % 12) + 1;
  const eMonAbs = startMonth + 2;
  const eYear = sy + Math.floor(eMonAbs / 12);
  const eMon = (eMonAbs % 12) + 1;
  return {
    from_date: `${sYear}-${pad(sMon)}-01`,
    to_date: `${eYear}-${pad(eMon)}-${lastDay(eYear, eMon)}`,
    label: `FY ${sy}-${String(sy + 1).slice(2)} · Q${qi + 1}`,
  };
};

export const PERIOD_OPTIONS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'tomorrow', label: 'Tomorrow' },
  { key: 'this_week', label: 'This Week' },
  { key: 'this_month', label: 'This Month' },
  { key: 'this_quarter', label: 'This Quarter' },
  { key: 'prev_quarter', label: 'Previous Quarter' },
  { key: 'this_fy', label: 'This FY' },
  { key: 'pick_month', label: 'Choose Month' },
  { key: 'custom', label: 'Custom Date Range' },
];

export function rangeFor(key, { month, from, to } = {}) {
  const t = new Date();
  if (key === 'today') return { from_date: iso(t), to_date: iso(t), label: `Today · ${fmtDate(iso(t))}` };
  if (key === 'yesterday') {
    const d = new Date(t); d.setDate(t.getDate() - 1);
    return { from_date: iso(d), to_date: iso(d), label: `Yesterday · ${fmtDate(iso(d))}` };
  }
  if (key === 'tomorrow') {
    const d = new Date(t); d.setDate(t.getDate() + 1);
    return { from_date: iso(d), to_date: iso(d), label: `Tomorrow · ${fmtDate(iso(d))}` };
  }
  if (key === 'this_week') {
    const dow = (t.getDay() + 6) % 7;
    const mon = new Date(t); mon.setDate(t.getDate() - dow);
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    return { from_date: iso(mon), to_date: iso(sun), label: `This Week · ${fmtDate(iso(mon))} → ${fmtDate(iso(sun))}` };
  }
  if (key === 'this_month') {
    const y = t.getFullYear(), m = t.getMonth() + 1;
    return { from_date: `${y}-${pad(m)}-01`, to_date: `${y}-${pad(m)}-${lastDay(y, m)}`, label: `This Month · ${t.toLocaleString('en', { month: 'long', year: 'numeric' })}` };
  }
  if (key === 'this_quarter') { const r = quarterRange(fyStart(t), qIndexOf(t)); return { ...r, label: `This Quarter · ${r.label}` }; }
  if (key === 'prev_quarter') {
    const qi = qIndexOf(t); const sy = fyStart(t);
    const r = qi === 0 ? quarterRange(sy - 1, 3) : quarterRange(sy, qi - 1);
    return { ...r, label: `Previous Quarter · ${r.label}` };
  }
  if (key === 'this_fy') {
    const sy = fyStart(t);
    return { from_date: `${sy}-04-01`, to_date: `${sy + 1}-03-31`, label: `This FY · FY ${sy}-${String(sy + 1).slice(2)}` };
  }
  if (key === 'pick_month') {
    const ym = month || iso(t).slice(0, 7);
    const [y, m] = ym.split('-').map(Number);
    return { from_date: `${y}-${pad(m)}-01`, to_date: `${y}-${pad(m)}-${lastDay(y, m)}`, label: new Date(y, m - 1, 1).toLocaleString('en', { month: 'long', year: 'numeric' }) };
  }
  const f = from || iso(t), tt = to || iso(t);
  return { from_date: f, to_date: tt, label: `${fmtDate(f)} → ${fmtDate(tt)}` };
}

// last 24 months as a plain list (Safari has no native month picker)
const MONTH_OPTS = Array.from({ length: 24 }, (_, i) => {
  const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - i);
  return { v: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, l: d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }) };
});

/** Polished single-dropdown period filter. Calls onChange({key, from_date, to_date, label}). */
export default function DemoPeriodSelector({ onChange, defaultKey = 'this_month' }) {
  const [key, setKey] = useState(defaultKey);
  const [month, setMonth] = useState(MONTH_OPTS[0].v);
  const [from, setFrom] = useState(new Date().toISOString().slice(0, 10));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));

  const range = useMemo(() => ({ key, ...rangeFor(key, { month, from, to }) }), [key, month, from, to]);
  useEffect(() => { onChange?.(range); }, [range]); // eslint-disable-line

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="demo-period-filter">
      <div className="relative">
        <CalendarRange className="w-3.5 h-3.5 text-violet-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <Select value={key} onValueChange={setKey}>
          <SelectTrigger className="h-10 w-[208px] pl-9 bg-white border-slate-200 text-[13px] font-semibold text-slate-800 shadow-sm focus:ring-2 focus:ring-violet-500" data-testid="demo-period-trigger">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white">
            {PERIOD_OPTIONS.map((o) => (
              <SelectItem key={o.key} value={o.key} className="text-[13px]" data-testid={`period-opt-${o.key}`}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {key === 'pick_month' && (
        <select value={month} onChange={(e) => setMonth(e.target.value)} className="h-10 w-[150px] bg-white border border-slate-200 rounded-md text-sm px-2" data-testid="demo-period-month">
          {MONTH_OPTS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
        </select>
      )}
      {key === 'custom' && (
        <>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-10 w-[140px] bg-white" data-testid="demo-period-from" />
          <span className="text-slate-400 text-xs">to</span>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-10 w-[140px] bg-white" data-testid="demo-period-to" />
        </>
      )}
      <span className="text-[11px] font-medium text-slate-500 bg-slate-100/80 px-2.5 py-1.5 rounded-md" data-testid="demo-period-label">{range.label}</span>
    </div>
  );
}
